import React from 'react';
import { usePortalLanguage } from '../../../contexts/PortalLanguageContext';

const formatBytes = (bytes) => {
    if (typeof bytes !== 'number' || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i += 1;
    }
    return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatUploaded = (ms, locale) => {
    if (typeof ms !== 'number') return '';
    return new Date(ms).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
};

function PortalFilesCard({ files = [], sessionToken, getFileUrl, onSessionExpired, isCompactLayout }) {
    const { t, locale } = usePortalLanguage();
    const [rowState, setRowState] = React.useState({}); // { [fileId]: { loading, error } }

    if (!files || files.length === 0) {
        return (
            <div className={`rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-sm ${isCompactLayout ? 'p-5' : 'p-6'}`}>
                <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{t('Files')}</h2>
                <p className="mt-3 text-sm text-[color:var(--text-muted)]">{t('No files have been shared yet.')}</p>
            </div>
        );
    }

    const handleOpen = async (file) => {
        setRowState(prev => ({ ...prev, [file.id]: { loading: true, error: '' } }));
        try {
            const response = await getFileUrl({ sessionToken: sessionToken?.jwt, fileId: file.id });
            const url = response?.data?.url;
            if (!url) throw new Error('No URL returned.');
            window.open(url, '_blank', 'noopener');
            setRowState(prev => ({ ...prev, [file.id]: { loading: false, error: '' } }));
        } catch (error) {
            const code = error?.code || '';
            if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
                onSessionExpired?.();
                setRowState(prev => ({ ...prev, [file.id]: { loading: false, error: '' } }));
                return;
            }
            const msg = code === 'functions/not-found'
                ? 'File is no longer available.'
                : "Couldn't open file. Try again.";
            setRowState(prev => ({ ...prev, [file.id]: { loading: false, error: msg } }));
        }
    };

    return (
        <div className={`rounded-2xl border border-[color:var(--border-color)] bg-[color:var(--surface-primary)] shadow-sm ${isCompactLayout ? 'p-5' : 'p-6'}`}>
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{t('Files')}</h2>
            <ul className="mt-4 divide-y divide-[color:var(--border-color)]">
                {files.map(file => {
                    const state = rowState[file.id] || {};
                    return (
                        <li key={file.id} className="py-3 flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                                <button
                                    type="button"
                                    onClick={() => handleOpen(file)}
                                    disabled={state.loading}
                                    className="text-left text-sm font-medium text-[color:var(--color-accent)] hover:underline disabled:opacity-60 truncate block w-full"
                                >
                                    {file.name}
                                </button>
                                <div className="mt-0.5 text-xs text-[color:var(--text-muted)] flex gap-3">
                                    {formatBytes(file.size) && <span>{formatBytes(file.size)}</span>}
                                    {formatUploaded(file.uploadedAt, locale) && <span>{t('Uploaded {date}', { date: formatUploaded(file.uploadedAt, locale) })}</span>}
                                </div>
                                {state.error && (
                                    <p className="mt-1 text-xs text-[color:var(--color-danger)]">{t(state.error)}</p>
                                )}
                            </div>
                            {state.loading && (
                                <span className="text-xs text-[color:var(--text-muted)]">{t('Opening…')}</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default PortalFilesCard;
